package com.arena_web.ui.sections.eventdetail

import androidx.compose.runtime.*
import com.arena_web.data.dto.EventMediaDto
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.icons.TintedGlyph
import com.arena_web.ui.components.widgets.MediaLightbox
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Video

@Composable
fun MatchMedia(
    mediaItems: List<EventMediaDto>,
    isLoading: Boolean,
    /**
     * Current user id — drives the per-tile delete affordance together with
     * [isAdmin]. Null when no one is signed in; tiles render read-only.
     */
    currentUserId: String? = null,
    /**
     * Whether the caller is admin of this event. Admins can delete any tile;
     * regular participants can only delete tiles they uploaded themselves
     * (matches the backend `DeleteEventMediaUseCase` rule).
     */
    isAdmin: Boolean = false,
    onDeleteMedia: (mediaId: String) -> Unit = {},
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current
    if (isLoading) {
        Box(
            modifier = modifier.fillMaxWidth().flexGrow(1),
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                t.eventLoadingMedia,
                modifier = Modifier
                    .sFontSize(18)
                    .fontWeight(FontWeight.Medium)
                    .color(AppColors.Text.Secondary)
            )
        }
        return
    }

    if (mediaItems.isEmpty()) {
        MediaEmptyState(modifier = modifier)
        return
    }

    // Split into rows of 3
    val rows = mediaItems.chunked(3)

    Column(
        modifier = modifier
            .fillMaxWidth()
            .sGap(20)
            .styleModifier {
                property("overflow-y", "auto")
                property("overflow-x", "hidden")
                property("scrollbar-width", "thin")
                property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
            }
    ) {
        rows.forEachIndexed { rowIndex, rowItems ->
            val isOddRow = rowIndex % 2 == 0 // 0, 2, 4… = "wide first" pattern
            // Odd rows (0,2,4): first item 934px, flex-1 height
            // Even rows (1,3,5): first item 535px, fixed 396px height

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .sHeight(396)
                    .sGap(20)
            ) {
                rowItems.forEachIndexed { colIndex, media ->
                    val isVideo = media.mediaType.uppercase() == "VIDEO"

                    val canDelete = isAdmin || (currentUserId != null && media.uploaderId == currentUserId)
                    if (colIndex == 0) {
                        // First item: fixed width
                        MediaItem(
                            media = media,
                            isVideo = isVideo,
                            canDelete = canDelete,
                            onDelete = { onDeleteMedia(media.id) },
                            modifier = Modifier
                                .fillMaxHeight()
                                .then(
                                    if (isOddRow)
                                        Modifier.sWidth(934)
                                    else
                                        Modifier.sWidth(535)
                                )
                                .styleModifier { property("flex-shrink", "0") }
                        )
                    } else {
                        // Other items: flex-1
                        MediaItem(
                            media = media,
                            isVideo = isVideo,
                            canDelete = canDelete,
                            onDelete = { onDeleteMedia(media.id) },
                            modifier = Modifier
                                .fillMaxHeight()
                                .styleModifier { property("flex", "1 1 0px") }
                                .minWidth(0.px)
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun MediaItem(
    media: EventMediaDto,
    isVideo: Boolean,
    canDelete: Boolean = false,
    onDelete: () -> Unit = {},
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current
    var expanded by remember { mutableStateOf(false) }
    var confirmDelete by remember { mutableStateOf(false) }

    Box(
        modifier = modifier
            .sBorderRadius(16)
            .overflow(Overflow.Hidden)
            .position(Position.Relative)
            .cursor(Cursor.Pointer)
            .onClick { expanded = true },
        contentAlignment = Alignment.Center
    ) {
        if (isVideo) {
            // `#t=0.1` seeks 100 ms in so the first frame paints as the
            // poster — without it most browsers show a black box until
            // the user starts playback.
            Video(
                attrs = {
                    attr("src", "${media.url}#t=0.1")
                    attr("preload", "metadata")
                    attr("muted", "")
                    attr("playsinline", "")
                    style {
                        property("position", "absolute")
                        property("inset", "0")
                        property("width", "100%")
                        property("height", "100%")
                        property("object-fit", "cover")
                        property("pointer-events", "none")
                    }
                }
            )
            // Positioned + z-index so the play-glyph sits above the absolute
            // <video> (CSS painting order otherwise puts positioned elements
            // above in-flow siblings). Dark circular backdrop keeps the
            // white triangle readable on bright frames.
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .sSize(96)
                    .borderRadius(50.percent)
                    .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgba(0, 0, 0, 0.45f))
                    .zIndex(1),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    src = AppIcons.Play,
                    modifier = Modifier.sSize(40)
                )
            }
        } else {
            Image(
                src = media.url,
                modifier = Modifier
                    .fillMaxSize()
                    .styleModifier { property("object-fit", "cover") }
            )
        }

        if (canDelete) {
            // Top-right trash badge — stops propagation so the tile's tap
            // doesn't also open the lightbox.
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .sTop(12)
                    .sRight(12)
                    .sSize(36)
                    .borderRadius(50.percent)
                    .cursor(Cursor.Pointer)
                    .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgba(0, 0, 0, 0.55f))
                    .zIndex(2)
                    .onClick {
                        it.stopPropagation()
                        confirmDelete = true
                    },
                contentAlignment = Alignment.Center
            ) {
                Icon(src = AppIcons.Trash, modifier = Modifier.sSize(18))
            }
        }
    }

    com.arena_web.ui.components.events.MatchActionConfirmDialog(
        isOpen = confirmDelete,
        title = t.eventDeleteMediaQuestion,
        message = t.eventDeleteMediaMessage,
        confirmText = t.commonDelete,
        iconSrc = AppIcons.Trash,
        iconAccent = "#F72A0A",
        isBusy = false,
        busyText = t.eventDeleting,
        onCancel = { confirmDelete = false },
        onConfirm = {
            onDelete()
            confirmDelete = false
        },
    )

    if (expanded) {
        MediaLightbox(
            url = media.url,
            isVideo = isVideo,
            onDismiss = { expanded = false }
        )
    }
}

@Composable
private fun MediaEmptyState(modifier: Modifier = Modifier) {
    val t = LocalAppStrings.current
    Column(
        modifier = modifier
            .fillMaxWidth()
            .flexGrow(1)
            .sGap(24),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        TintedGlyph(src = AppIcons.Videocam, sizePx = 80)

        SpanText(
            t.eventNoMediaYet,
            modifier = Modifier
                .sFontSize(24)
                .fontWeight(FontWeight.Medium)
                .sLineHeight(32)
                .color(AppColors.Text.Secondary)
                .styleModifier { property("letter-spacing", s(0.24)) }
        )

        SpanText(
            t.eventNoMediaDescription,
            modifier = Modifier
                .sFontSize(18)
                .fontWeight(FontWeight.Medium)
                .sLineHeight(22)
                .color(AppColors.Text.Muted)
                .styleModifier {
                    property("letter-spacing", s(0.09))
                    property("text-align", "center")
                    property("max-width", s(400))
                }
        )
    }
}
